import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, ApiError, uploadFileToSignedUrl } from '@l2u/ui/lib/api.ts';
import { ApprovedLine } from '@l2u/ui/components/ApprovedLine.tsx';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { Modal } from '@l2u/ui/components/Modal.tsx';
import { CommentThread } from '@l2u/ui/components/CommentThread.tsx';
import { DocumentTable } from '@l2u/ui/components/DocumentTable.tsx';
import { ReplyComposer, type ReplyPayload } from '@l2u/ui/components/ReplyComposer.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import type { DocVersionFile } from '@l2u/ui/lib/documentVersions.ts';
import { formatLocalDay } from '@l2u/ui/lib/expiry.ts';
import type { Question, SubmissionComment } from '@l2u/ui/lib/types.ts';
import {
  replyAttachmentCanPromote,
  replyAttachmentOffersExpiry,
} from '@l2u/shared/replyAttachment';
import { partitionThread } from '@l2u/shared/threadPartition';
import { invalidateCompany } from '../lib/invalidateCompany.ts';
import {
  canCloseConversation,
  closedLineAt,
  conversationIsOpen,
  seenThrough,
  withAdminReply,
  withConversationClosed,
  type ConversationFields,
  type SubmissionsCache,
} from './conversationClose.ts';

// CommentModal renders the admin↔vendor thread for a submission, the document
// version table for the question's files, and a reply composer that lets the
// admin attach one or more files (each tagged reference / replaces / new)
// alongside an optional message.
//
// For a Documents-tab document with recorded approvals (`approvals` non-null)
// the thread is cut at those approval moments (partitionThread), and each tab
// shows the slice it owns: the Compliance Documents tab shows the current
// conversation (unpublished drafts included) with the composer, or an empty
// state naming the latest approval; the Questionnaire tab shows the original
// review read-only, with the first approval's "Approved {date}" line. A
// composer there would post into a conversation that popup doesn't show.
// `approvals === null` shows the whole thread and the composer, as before.
//
// On an approved compliance document (live status, conversationClose.ts) the
// admin can close the conversation: a "Close conversation" button while the
// round is open, or the composer's "No reply needed" checkbox, and a closed
// conversation shows a "Conversation closed {date}" line. Writing a new
// comment reopens it, so the composer stays; the read-only questionnaire
// popup shows none of this.
export function CommentModal({
  question,
  companyId,
  submissionId,
  submission,
  onClose,
  scope,
  approvals,
  initialCloseConflict = false,
}: {
  question: Question;
  companyId: string;
  submissionId: string | undefined;
  /** The row's submission (undefined when unanswered): drives the close controls and the closed line. */
  submission: ConversationFields | undefined;
  onClose: () => void;
  /** Which tab opened the popup. Only matters when `approvals` is non-null. */
  scope: 'questionnaire' | 'documents';
  /** The document's approval moments when it is a Documents-tab document on its LIVE status (adminDocumentsTabApprovals, Task 8); null = whole thread + composer, as today. */
  approvals: string[] | null;
  /** Test seam (no DOM harness): start as if the close endpoint had just answered 409. */
  initialCloseConflict?: boolean;
}) {
  const qc = useQueryClient();
  const [closeConflict, setCloseConflict] = useState(initialCloseConflict);

  // One prefix covers this thread, its file history, the submissions list and
  // pending-changes; invalidating them one by one fetched each of them twice.
  const invalidateAfterWrite = () => invalidateCompany(qc, companyId);

  // The close controls read this popup's row from the page's submissions list,
  // whose re-fetch takes ~2 s. Patch the cached row from what the write proved
  // so the popup updates at once; invalidateAfterWrite still re-fetches the truth.
  const patchSubmissionsCache = (
    patch: (d: SubmissionsCache | undefined, id: string) => SubmissionsCache | undefined,
  ) => {
    if (!submissionId) return;
    qc.setQueryData<SubmissionsCache>(['company', companyId, 'submissions'], (d) =>
      patch(d, submissionId),
    );
  };

  const commentsKey = ['company', companyId, 'submissions', submissionId, 'comments'];
  const commentsQuery = useQuery({
    queryKey: commentsKey,
    queryFn: () =>
      adminApi.get<{ comments: SubmissionComment[] }>(
        `/api/companies/${companyId}/submissions/${submissionId}/comments`,
      ),
    enabled: !!submissionId,
    retry: 1,
  });

  const filesHistoryQuery = useQuery({
    queryKey: ['company', companyId, 'submission', submissionId, 'file-history'],
    queryFn: () =>
      adminApi.get<{ files: DocVersionFile[] }>(
        `/api/companies/${companyId}/submissions/${submissionId}/files/history`,
      ),
    enabled: !!submissionId,
    retry: 1,
  });

  const postMutation = useMutation({
    mutationFn: async (payload: ReplyPayload) => {
      const attachments = [];
      for (const a of payload.attachments) {
        const presigned = await adminApi.post<{ path: string; url: string }>(
          `/api/companies/${companyId}/submissions/${question.id}/upload-url`,
          { file_name: a.file.name, mime_type: a.file.type },
        );
        await uploadFileToSignedUrl(presigned.url, a.file);
        attachments.push({
          attachment_path: presigned.path,
          attachment_name: a.file.name,
          attachment_size_bytes: a.file.size,
          attachment_mime_type: a.file.type || 'application/octet-stream',
          attachment_expires_at: a.expiresAt ?? undefined,
          replaces_file_ids: a.replacesFileIds,
          mode: a.mode,
        });
      }
      return adminApi.post(`/api/companies/${companyId}/submissions/${submissionId}/comments`, {
        ...(payload.body ? { body: payload.body } : {}),
        ...(attachments.length ? { attachments } : {}),
        ...(payload.closesConversation ? { closes_conversation: true } : {}),
      });
    },
    onSuccess: () => {
      // A sent comment answers whatever the 409 notice asked the admin to read.
      setCloseConflict(false);
      patchSubmissionsCache(withAdminReply);
      invalidateAfterWrite();
    },
  });

  // Fix the text of a reviewer comment Notify has not sent yet. The server
  // also rewrites the queued email copy. Patch the cached thread from what the
  // write proved so the bubble shows the new text at once; a 409 (Notify sent
  // it first) re-fetches so the Edit link disappears. CommentThread shows the
  // error in the edit box.
  const editBody = async (commentId: string, body: string) => {
    try {
      await adminApi.patch(
        `/api/companies/${companyId}/submissions/${submissionId}/comments/${commentId}`,
        { body },
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) void commentsQuery.refetch();
      throw err;
    }
    qc.setQueryData<{ comments: SubmissionComment[] }>(commentsKey, (d) =>
      d ? { comments: d.comments.map((cm) => (cm.id === commentId ? { ...cm, body } : cm)) } : d,
    );
    invalidateAfterWrite();
  };

  const comments = commentsQuery.data?.comments ?? [];
  const slices = approvals === null ? null : partitionThread(comments, approvals);
  const readOnly = approvals !== null && scope === 'questionnaire';
  const canClose = canCloseConversation(question, submission) && !readOnly;
  const closedAt = readOnly ? null : closedLineAt(submission);

  // seen_through covers the WHOLE fetched thread (History included): the server
  // refuses with 409 when any vendor comment is newer than it. The button waits
  // for the thread to load, so it never sends a blind null.
  const closeMutation = useMutation({
    mutationFn: () =>
      adminApi.post<{ conversation_closed_at: string | null }>(
        `/api/companies/${companyId}/submissions/${submissionId}/close-conversation`,
        { seen_through: seenThrough(comments) },
      ),
    onMutate: () => setCloseConflict(false),
    onSuccess: (res) => {
      patchSubmissionsCache((d, id) => withConversationClosed(d, id, res.conversation_closed_at));
      invalidateAfterWrite();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setCloseConflict(true);
        void commentsQuery.refetch();
      }
    },
  });
  const closeError =
    closeMutation.error instanceof ApiError && closeMutation.error.status === 409
      ? null
      : closeMutation.error;

  let thread: ReactNode;
  if (approvals === null || slices === null) {
    thread = <CommentThread comments={comments} onEditBody={editBody} />;
  } else if (readOnly) {
    thread = (
      <div>
        {/* An empty original review shows no thread: CommentThread's empty state ("No messages yet.") would read as a blank conversation. */}
        {slices.original.length > 0 && <CommentThread comments={slices.original} />}
        <ApprovedLine at={approvals[0]!} />
        <p className="text-xs text-slate-500">
          Newer comments are on the Compliance Documents tab.
        </p>
      </div>
    );
  } else if (slices.current.length > 0) {
    thread = <CommentThread comments={slices.current} onEditBody={editBody} />;
  } else {
    thread = (
      <div className="space-y-1">
        <p className="text-sm text-slate-600">
          No comments since this document was approved on {formatLocalDay(approvals.at(-1)!)}.
        </p>
        <p className="text-xs text-slate-500">
          Earlier conversations are in History. The original review is on the Questionnaire tab.
        </p>
      </div>
    );
  }

  return (
    <Modal
      open
      title={`Comments — ${question.label}`}
      size="lg"
      onClose={onClose}
      footer={
        <Btn size="sm" variant="ghost" onClick={onClose}>
          Close
        </Btn>
      }
    >
      {commentsQuery.isError ? (
        <div className="space-y-2">
          <ErrorMessage error={commentsQuery.error} />
          <button
            type="button"
            className="text-xs text-[#4D148C] underline hover:text-[#3B0F6B]"
            onClick={() => void commentsQuery.refetch()}
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {(filesHistoryQuery.data?.files.length ?? 0) > 0 && (
            <DocumentTable
              files={filesHistoryQuery.data!.files}
              requiresExpiry={question.requires_expiry}
            />
          )}
          {thread}
          {closedAt && (
            <p className="text-xs text-slate-500">
              Conversation closed {formatLocalDay(closedAt)}. Sending a new comment reopens it.
            </p>
          )}
          {canClose && submissionId && (
            <div className="space-y-2">
              {conversationIsOpen(submission) && (
                <Btn
                  size="sm"
                  variant="secondary"
                  loading={closeMutation.isPending}
                  disabled={!commentsQuery.isSuccess}
                  onClick={() => closeMutation.mutate()}
                >
                  Close conversation
                </Btn>
              )}
              {closeConflict && (
                <p className="text-xs text-amber-700">
                  The vendor has replied since you opened this. Read the new message, then decide
                  again.
                </p>
              )}
              <ErrorMessage error={closeError} />
            </div>
          )}
          {submissionId && !readOnly && (
            <ReplyComposer
              role="admin"
              canPromote={replyAttachmentCanPromote(question)}
              offersExpiry={replyAttachmentOffersExpiry(question)}
              allowMultipleFiles={question.allow_multiple_files ?? true}
              currentFiles={filesHistoryQuery.data?.files ?? []}
              offersClose={canClose}
              onReply={(p) => postMutation.mutate(p)}
              pending={postMutation.isPending}
              error={postMutation.error as Error | null}
              requiresExpiry={question.requires_expiry}
            />
          )}
        </div>
      )}
    </Modal>
  );
}
